import { useState, useEffect } from 'react'
import {
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material'
import { Modal, Button } from '@components/common'
import api from '@services/api'

const STATUS_OPTIONS = [
  { value: 'operational', label: 'Operational' },
  { value: 'standby', label: 'Standby' },
  { value: 'maintenance', label: 'Under Maintenance' },
  { value: 'out_of_service', label: 'Out of Service' },
]

const FUEL_TYPE_OPTIONS = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'petrol', label: 'Petrol' },
  { value: 'natural_gas', label: 'Natural Gas' },
]

const initialFormData = {
  name: '',
  model: '',
  serial_number: '',
  capacity_kva: '',
  fuel_type: 'diesel',
  fuel_tank_capacity: '',
  location: '',
  status: 'operational',
  installation_date: '',
  last_service_date: '',
  next_service_date: '',
  notes: '',
}

const GeneratorForm = ({ open, onClose, generator, onSuccess }) => {
  const [formData, setFormData] = useState(initialFormData)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const isEditing = Boolean(generator)

  useEffect(() => {
    if (open) {
      if (generator) {
        setFormData({
          name: generator.name || '',
          model: generator.model || '',
          serial_number: generator.serial_number || '',
          capacity_kva: generator.capacity_kva || '',
          fuel_type: generator.fuel_type || 'diesel',
          fuel_tank_capacity: generator.fuel_tank_capacity || '',
          location: generator.location || '',
          status: generator.status || 'operational',
          installation_date: generator.installation_date || '',
          last_service_date: generator.last_service_date || '',
          next_service_date: generator.next_service_date || '',
          notes: generator.notes || '',
        })
      } else {
        setFormData(initialFormData)
      }
      setErrors({})
    }
  }, [open, generator])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setLoading(true)
    try {
      const payload = {
        ...formData,
        capacity_kva: formData.capacity_kva || null,
        fuel_tank_capacity: formData.fuel_tank_capacity || null,
      }

      if (isEditing) {
        await api.patch(`/facilities/generators/${generator.id}/`, payload)
      } else {
        await api.post('/facilities/generators/', payload)
      }

      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error saving generator:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Generator' : 'Add Generator'}
      maxWidth="md"
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Generator Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Model"
            name="model"
            value={formData.model}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Serial Number"
            name="serial_number"
            value={formData.serial_number}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Capacity (KVA)"
            name="capacity_kva"
            type="number"
            value={formData.capacity_kva}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Fuel Type</InputLabel>
            <Select
              name="fuel_type"
              value={formData.fuel_type}
              onChange={handleChange}
              label="Fuel Type"
            >
              {FUEL_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Fuel Tank Capacity (L)"
            name="fuel_tank_capacity"
            type="number"
            value={formData.fuel_tank_capacity}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={formData.status}
              onChange={handleChange}
              label="Status"
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Installation Date"
            name="installation_date"
            type="date"
            value={formData.installation_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Last Service Date"
            name="last_service_date"
            type="date"
            value={formData.last_service_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Next Service Date"
            name="next_service_date"
            type="date"
            value={formData.next_service_date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default GeneratorForm
